import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { AddLayerCommand, CloneStampCommand, FillRasterCommand, HealStrokeCommand, HistoryBrushStrokeCommand, PaintMaskStrokeCommand, PaintStrokeCommand, QuickMaskStrokeCommand, SelectionCommand, SetActiveLayersCommand, TransformLayerCommand, isMatrixTransformCommand, type BrushSettings, type MatrixTransformCommand } from '../core/commands';
import type { RasterTileStore } from '../core/tiles';
import type { SelectionState } from '../core/selection';
import type { Artboard } from '../core/export-resources';
import { findLayer, findLayersAtPoint, layerBounds, layerDocumentTransform, layerParentTransform, vectorObjectLocalBounds, walkLayers, type EditorDocument, type Layer, type TextLayer } from '../core/model';
import { IDENTITY_MATRIX, invertMatrix, matrixAround, multiplyMatrix, snapRectTranslation, transformPoint, transformRectBoundsPrecise, type Matrix, type Point, type Rect } from '../core/geometry';
import { expandToGroups, groupIdOf } from '../vector/object-groups';
import { createExtensionMultiScaleCommand, createExtensionMultiTransformCommand, createExtensionPathNodeTransformCommand, createExtensionPathSegmentTransformCommand, createExtensionScaleCommand, createExtensionTextEditCommand, createExtensionTransformCommand, getExtensionObject, hitTestExtensionTargets, isTextEditCommand, outlineExtensionSelection, selectExtensionWithinRect, type ExtensionObjectTarget, type InteractionTarget } from '../render/extension-interactions';
import type { UpdateTextCommand as TextPatchCommand } from '../vector/runtime';
import { Canvas2DRenderer, type RenderStats, type Viewport } from '../render/renderer';
import type { PointerReadout, ToolId, VectorObjectSelection } from './types';
import type { ChannelView } from './ChannelsPanel';
import { activePerspectivePlane, PerspectiveCloneStampCommand, perspectiveGridLines, perspectivePlanes as documentPerspectivePlanes, SetPerspectivePlanesCommand, validPerspectiveQuad, type PerspectivePlane, type Quad } from '../core/perspective';
import { QuickSelectionCommand } from '../core/quick-selection';
import type { DocumentNote } from '../core/notes';
import { ExposureStrokeCommand, type ExposureSettings } from '../core/exposure-tools';
import { transformForPlacement, vectorDrawPlacement, type VectorDrawMode } from '../core/vector-draw-mode';
import type { GridToolSettings, ShapeToolKind } from '../core/tool-presets';
import { isolationDocument, layerInIsolation, type IsolationScope } from './isolation';

interface CanvasWorkspaceProps {
  document: EditorDocument;
  onEditPortableText: (layer: TextLayer, point: Point | null) => void;
  history: import('../core/commands').HistoryManager;
  tool: ToolId;
  temporaryHand: boolean;
  brush: BrushSettings;
  quickSelectionTolerance: number;
  exposureSettings: ExposureSettings;
  notes: readonly DocumentNote[];
  onCreateNote: (point: Point) => void;
  historyBrushSource: RasterTileStore | null;
  quickMaskActive: boolean;
  channelView: ChannelView;
  alphaChannelSelection: SelectionState | null;
  artboards: readonly Artboard[];
  perspectivePlanes: readonly PerspectivePlane[];
  perspectiveClone: boolean;
  backgroundColor: string;
  maskEditingLayerId: string | null;
  vectorObjectSelection: VectorObjectSelection | null;
  groupEntry: { layerId: string; groupId: string } | null;
  onGroupEntry: (entry: { layerId: string; groupId: string } | null) => void;
  onVectorObjectSelection: (selection: VectorObjectSelection | null) => void;
  isolationScope: IsolationScope | null;
  onIsolationScope: (scope: IsolationScope | null) => void;
  toolBlockedReason: string | null;
  onDirectTextEditingChange: (layerId: string | null) => void;
  showGrid: boolean;
  showGuides: boolean;
  showRulers: boolean;
  snapEnabled: boolean;
  scaleStrokesEffects: boolean;
  selectionMode: import('../core/selection').SelectionCombineMode;
  shapeKind: ShapeToolKind;
  gridSettings: GridToolSettings;
  vectorDrawMode: VectorDrawMode;
  revision: number;
  onPointerReadout: (readout: PointerReadout) => void;
  onViewport: (viewport: Viewport) => void;
  onRenderStats: (stats: RenderStats) => void;
  viewAction: { id: number; action: 'fit' | 'in' | 'out' };
}

interface Interaction {
  kind: 'paint' | 'move' | 'resize' | 'rotate' | 'select' | 'object-band' | 'shape' | 'pen' | 'shaper' | 'knife' | 'pan' | 'gradient';
  startScreen: Point;
  startDocument: Point;
  originalTransform?: Matrix;
  parentTransform?: Matrix;
  transformBounds?: Rect;
  resizeHandle?: ResizeHandle;
  transformCenter?: Point;
  changed?: boolean;
  targetLayerId?: string;
  targetObjectId?: string;
  textBoxOriginal?: { width: number; height: number; autoHeight: boolean };
  /** Object ids the band extends, captured at press so releasing Shift mid-drag cannot change the gesture. */
  bandBaseObjectIds?: string[];
}

const resizeHandles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type ResizeHandle = typeof resizeHandles[number];

function isResizeHandle(value: string | null): value is ResizeHandle {
  return resizeHandles.some((handle) => handle === value);
}

function handlePoint(bounds: Rect, handle: ResizeHandle): Point {
  const horizontal = handle.includes('w') ? bounds.x : handle.includes('e') ? bounds.x + bounds.width : bounds.x + bounds.width / 2;
  const vertical = handle.includes('n') ? bounds.y : handle.includes('s') ? bounds.y + bounds.height : bounds.y + bounds.height / 2;
  return { x: horizontal, y: vertical };
}

function oppositeHandlePoint(bounds: Rect, handle: ResizeHandle): Point {
  const horizontal = handle.includes('w') ? bounds.x + bounds.width : handle.includes('e') ? bounds.x : bounds.x + bounds.width / 2;
  const vertical = handle.includes('n') ? bounds.y + bounds.height : handle.includes('s') ? bounds.y : bounds.y + bounds.height / 2;
  return { x: horizontal, y: vertical };
}

function nonZeroScale(value: number): number {
  if (Math.abs(value) >= 0.01) return value;
  return value < 0 ? -0.01 : 0.01;
}

function shapeRect(start: Point, end: Point, constrained: boolean, fromCenter: boolean): Rect {
  let dx = end.x - start.x;
  let dy = end.y - start.y;
  if (constrained) {
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    dx = (dx < 0 ? -1 : 1) * size;
    dy = (dy < 0 ? -1 : 1) * size;
  }
  const opposite = fromCenter ? { x: start.x - dx, y: start.y - dy } : start;
  const corner = { x: start.x + dx, y: start.y + dy };
  return {
    x: Math.min(opposite.x, corner.x),
    y: Math.min(opposite.y, corner.y),
    width: Math.abs(corner.x - opposite.x),
    height: Math.abs(corner.y - opposite.y),
  };
}

interface CanvasTransformTarget {
  layer: Layer;
  bounds: Rect;
  transform: Matrix;
  parentTransform: Matrix;
  object?: ExtensionObjectTarget | InteractionTarget;
  collectiveObjectIds?: readonly string[];
}

interface SpatialSnapTarget { layerId: string; objectId?: string; bounds: Rect }

function targetCommand(target: CanvasTransformTarget): MatrixTransformCommand | null {
  return target.object && 'scope' in target.object && target.object.scope === 'path-node' && target.object.detailId
    ? createExtensionPathNodeTransformCommand(target.layer, target.object.id, [target.object.detailId])
    : target.object && 'scope' in target.object && target.object.scope === 'path-segment' && target.object.detailId
    ? createExtensionPathSegmentTransformCommand(target.layer, target.object.id, target.object.detailId)
    : target.collectiveObjectIds
    ? createExtensionMultiTransformCommand(target.layer, target.collectiveObjectIds)
    : target.object
    ? createExtensionTransformCommand(target.layer, target.object.id)
    : new TransformLayerCommand(target.layer.id);
}

const renderer = new Canvas2DRenderer();
const isolationRenderer = new Canvas2DRenderer();

function applyChannelPreview(canvas: HTMLCanvasElement, viewport: Viewport, view: ChannelView, alpha: SelectionState | null, documentWidth: number, documentHeight: number): void {
  if (view === 'composite') return;
  const context = canvas.getContext('2d');
  if (!context) return;
  if (view === 'red' || view === 'green' || view === 'blue') {
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    const channel = view === 'red' ? 0 : view === 'green' ? 1 : 2;
    for (let offset = 0; offset < image.data.length; offset += 4) {
      const value = image.data[offset + channel] ?? 0;
      image.data[offset] = value;
      image.data[offset + 1] = value;
      image.data[offset + 2] = value;
    }
    context.putImageData(image, 0, 0);
    return;
  }
  if (!alpha) return;
  context.save();
  context.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
  context.fillStyle = '#111';
  context.fillRect(0, 0, viewport.width, viewport.height);
  context.fillStyle = alpha.inverted ? '#fff' : '#000';
  context.fillRect(viewport.panX, viewport.panY, documentWidth * viewport.zoom, documentHeight * viewport.zoom);
  context.fillStyle = alpha.inverted ? '#000' : '#fff';
  for (const region of alpha.regions) context.fillRect(
    viewport.panX + region.x * viewport.zoom,
    viewport.panY + region.y * viewport.zoom,
    region.width * viewport.zoom,
    region.height * viewport.zoom,
  );
  context.restore();
}

function clampZoom(zoom: number): number {
  return Math.max(0.02, Math.min(64, zoom));
}

export function CanvasWorkspace({
  document,
  onEditPortableText,
  history,
  tool,
  temporaryHand,
  brush,
  quickSelectionTolerance,
  exposureSettings,
  notes,
  onCreateNote,
  historyBrushSource,
  quickMaskActive,
  channelView,
  alphaChannelSelection,
  artboards,
  perspectivePlanes,
  perspectiveClone,
  backgroundColor,
  maskEditingLayerId,
  vectorObjectSelection,
  groupEntry,
  onGroupEntry,
  onVectorObjectSelection,
  isolationScope,
  onIsolationScope,
  toolBlockedReason,
  onDirectTextEditingChange,
  showGrid,
  showGuides,
  showRulers,
  snapEnabled,
  scaleStrokesEffects,
  selectionMode,
  shapeKind,
  gridSettings,
  vectorDrawMode,
  revision,
  onPointerReadout,
  onViewport,
  onRenderStats,
  viewAction,
}: CanvasWorkspaceProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isolationCanvasRef = useRef<HTMLCanvasElement>(null);
  const selectionCanvasRef = useRef<HTMLCanvasElement>(null);
  const interaction = useRef<Interaction | null>(null);
  const lastInputAt = useRef<number | null>(null);
  const mounted = useRef(true);
  const hitCycle = useRef<{ signature: string; index: number; at: number; point: Point } | null>(null);
  const initializedDocument = useRef<string | null>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  const [view, setView] = useState({ zoom: 1, panX: 0, panY: 0 });
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const [cursor, setCursor] = useState<Point | null>(null);
  const [gradient, setGradient] = useState<{ start: Point; end: Point } | null>(null);
  const [shapePreview, setShapePreview] = useState<{ kind: ShapeToolKind; rect: Rect } | null>(null);
  const [knifePreview, setKnifePreview] = useState<{ start: Point; end: Point } | null>(null);
  const [smartGuides, setSmartGuides] = useState<{ vertical?: number; horizontal?: number; labels?: string[] } | null>(null);
  const [textEditor, setTextEditor] = useState<{ layerId: string; value: string } | null>(null);
  const [penDraft, setPenDraft] = useState<Array<Omit<import('../vector/types').PathNode, 'id' | 'subpathId'>>>([]);
  const [penPointer, setPenPointer] = useState<Point | null>(null);
  const [shaperPoints, setShaperPoints] = useState<Point[]>([]);
  const [cloneSource, setCloneSource] = useState<{ layerId: string; point: Point } | null>(null);
  const [cloneOffset, setCloneOffset] = useState<Point | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    onDirectTextEditingChange(textEditor?.layerId ?? null);
    return () => onDirectTextEditingChange(null);
  }, [onDirectTextEditingChange, textEditor?.layerId]);

  useEffect(() => {
    const cancelGesture = (event: KeyboardEvent) => {
      // This is a capture-phase listener, before a dialog can stop bubbling.
      // Modal Enter/Escape belongs to the dialog, never to a background gesture.
      if (event.defaultPrevented || globalThis.document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const current = interaction.current;
      if (event.key === 'Enter' && (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || (event.target instanceof HTMLElement && event.target.isContentEditable))) return;
      if (event.key === 'Enter' && penDraft.length >= 2) {
        event.preventDefault();
        void import('../vector/index').then((vector) => {
          if (!mounted.current) return;
          vector.activateVectorSubsystem();
          const layer = vector.createPathLayer(penDraft);
          const placement = vectorDrawPlacement(document, vectorDrawMode);
          layer.clipping = placement.clipping;
          layer.transform = transformForPlacement(layer.transform, placement);
          history.execute(document, new AddLayerCommand(layer, placement.parentId, placement.index));
          const object = layer.vector.objects[0];
          onVectorObjectSelection(object ? { layerId: layer.id, objectIds: [object.id], scope: 'node', nodeIds: [] } : null);
          setPenDraft([]);
          setPenPointer(null);
        });
        return;
      }
      if (event.key !== 'Escape') return;
      if (!current && penDraft.length === 0) {
        // Idle Escape is the universal deselect (unless typing in a field).
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || (event.target instanceof HTMLElement && event.target.isContentEditable)) return;
        if (document.activeLayerIds.length > 0) history.applyTransient(document, new SetActiveLayersCommand([]));
        onVectorObjectSelection(null);
        return;
      }
      if (current && (current.kind === 'paint' || current.kind === 'move' || current.kind === 'resize' || current.kind === 'rotate')) history.cancelLive(document);
      interaction.current = null;
      setPenDraft([]);
      setPenPointer(null);
      setShaperPoints([]);
      setMarquee(null);
      setGradient(null);
      setShapePreview(null);
      setKnifePreview(null);
      setSmartGuides(null);
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener('keydown', cancelGesture, true);
    return () => window.removeEventListener('keydown', cancelGesture, true);
  }, [document, history, onVectorObjectSelection, penDraft, vectorDrawMode]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({ width: Math.max(1, entry.contentRect.width), height: Math.max(1, entry.contentRect.height) });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const fit = () => {
    const padding = 72;
    const zoom = clampZoom(Math.min((size.width - padding) / document.width, (size.height - padding) / document.height, 1));
    setView({
      zoom,
      panX: (size.width - document.width * zoom) / 2,
      panY: (size.height - document.height * zoom) / 2,
    });
  };

  useEffect(() => {
    if (viewAction.id === 0) return;
    if (viewAction.action === 'fit') fit();
    else zoomAt({ x: size.width / 2, y: size.height / 2 }, viewAction.action === 'in' ? 1.25 : 0.8);
  // keyed on the action id so a
  // dispatched zoom or fit runs once. Adding `size` or `fit` would re-fire it on resize.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAction.id]);

  useEffect(() => {
    const geometryKey = `${document.id}:${document.width}:${document.height}`;
    if (size.width <= 1 || initializedDocument.current === geometryKey) return;
    initializedDocument.current = geometryKey;
    fit();
  // `fit` is recreated every
  // render; the ref guard above is what makes this run once per document geometry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.id, document.width, document.height, size.width, size.height]);

  const viewport: Viewport = {
    width: size.width,
    height: size.height,
    zoom: view.zoom,
    panX: view.panX,
    panY: view.panY,
    dpr: Math.min(window.devicePixelRatio || 1, 2.5),
  };
  // Corner handles let the plane be placed over the artwork directly; typing
  // eight percentages into a modal that covers the canvas cannot align a grid
  // to a photograph. The draft is local until pointer-up so the drag is one
  // history entry.
  const planeDragIndex = useRef<number | null>(null);
  const planeDraftRef = useRef<Quad | null>(null);
  const [planeDraft, setPlaneDraft] = useState<{ planeId: string; corners: Quad } | null>(null);
  const shownPlanes = useMemo(
    () => perspectivePlanes.map((plane) => (planeDraft?.planeId === plane.id ? { ...plane, corners: planeDraft.corners } : plane)),
    [perspectivePlanes, planeDraft],
  );
  const perspectiveGrid = useMemo(() => shownPlanes.flatMap((plane) => {
    // A folded draft has no valid homography; keep the last good grid lines away
    // rather than throwing out of render.
    if (!validPerspectiveQuad(plane.corners)) return [];
    return perspectiveGridLines(plane).map((line, index) => ({ id: `${plane.id}:${index}`, line }));
  }), [shownPlanes]);
  // Handles are only interactive with Move selected; otherwise a stray click
  // near a corner would hijack the brush, eraser, marquee and every other tool.
  const perspectiveHandlePlane = tool === 'move' ? shownPlanes[0] : undefined;
  // `revision` is deliberate and
  // load-bearing: the document is mutated in place, so its identity is stable and
  // `revision` is the only signal that its contents changed. Removing it stops updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isolatedView = useMemo(() => isolationScope ? isolationDocument(document, isolationScope) : null, [document, isolationScope, revision]);

  // Rebuilt on every history revision, and each vector entry walks every node
  // and handle of its object — so on a traced layer this scanned hundreds of
  // thousands of points after every edit, whether or not snapping was on.
  const spatialSnapTargets = useMemo<SpatialSnapTarget[]>(() => !snapEnabled ? [] : walkLayers(document.root).flatMap((layer): SpatialSnapTarget[] => {
    if (!layer.visible || layer.kind === 'group' || layer.kind === 'adjustment') return [];
    const effective = layerDocumentTransform(document.root, layer.id);
    if (!effective) return [];
    if (layer.kind === 'vector') return layer.vector.objects.filter((object) => object.visible).flatMap((object) => {
      const local = vectorObjectLocalBounds(object);
      if (!local) return [];
      return [{ layerId: layer.id, objectId: object.id, bounds: transformRectBoundsPrecise(multiplyMatrix(effective, object.transform), local) }];
    });
    const local = layerBounds(layer);
    return local ? [{ layerId: layer.id, bounds: transformRectBoundsPrecise(effective, local) }] : [];
  // `revision` is deliberate; see
  // isolatedView above. The document mutates in place, so identity never changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [document, revision, snapEnabled]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderer.schedule(document, canvas, viewport, (stats) => {
      applyChannelPreview(canvas, viewport, channelView, alphaChannelSelection, document.width, document.height);
      const inputAt = lastInputAt.current;
      lastInputAt.current = null;
      onRenderStats(inputAt === null ? stats : { ...stats, inputToPresentMs: performance.now() - inputAt });
    });
    onViewport(viewport);
    const isolationCanvas = isolationCanvasRef.current;
    if (isolationCanvas && isolatedView) isolationRenderer.schedule(isolatedView, isolationCanvas, { ...viewport, transparentBackground: true });
  // `onRenderStats`, `onViewport`
  // and `viewport` are rebuilt every render; depending on them would schedule a render
  // from inside a render. `revision` is the in-place-mutation signal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, revision, size.width, size.height, view.zoom, view.panX, view.panY, channelView, alphaChannelSelection, isolatedView]);

  useEffect(() => () => {
    if (isolatedView) isolationRenderer.disposeDocument(isolatedView);
  }, [isolatedView]);

  useEffect(() => {
    const unsubscribe = history.subscribe((event) => renderer.invalidate(document, event.damage));
    return () => {
      unsubscribe();
      renderer.disposeDocument(document);
    };
  }, [document, history]);

  // Selected-object indication.
  //
  // Repainted from a dependency-free layout effect through a rAF coalescer, NOT
  // from a dependency list. TransformVectorObjectsCommand mutates
  // object.transform in place during a live drag, so neither the document
  // identity nor the revision changes while dragging — a dep list would leave
  // the outline frozen where the drag started until pointer-up. The component
  // re-renders on every pointer move anyway (cursor, smart guides, readout), so
  // this runs often enough, and the coalescer bounds it to one paint per frame.
  const selectionFrame = useRef(0);
  useLayoutEffect(() => {
    const canvas = selectionCanvasRef.current;
    if (!canvas) return;
    const paint = () => {
      selectionFrame.current = 0;
      // The same viewport the artwork renderer uses, and the same transform
      // shape. Computing screen coordinates by hand here meant a second,
      // parallel implementation of something already solved — and it drew the
      // outline in the wrong place on a display whose device pixel ratio is not
      // 1, because this used the raw ratio while the renderer clamps it.
      const scale = viewport.dpr;
      const width = Math.max(1, Math.floor(viewport.width * scale));
      const height = Math.max(1, Math.floor(viewport.height * scale));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      // The CSS size has to be set too, and this is the whole bug that put the
      // outline out of frame. `.display-canvas` is `position: absolute; inset: 0`,
      // which looks like it stretches the element to its container — but the
      // `width` attribute is a presentational hint that sets the CSS `width`
      // property, and any length beats the `auto` that `inset` relies on. So the
      // element laid out at its BACKING-STORE size in CSS pixels: 2126 CSS px
      // wide on a 1063 px viewport at dpr 2, drawing everything at double scale
      // and pushing most of the outline off the visible canvas. `renderer.ts`
      // has always set both; this overlay set only one.
      if (canvas.style.width !== `${viewport.width}px`) canvas.style.width = `${viewport.width}px`;
      if (canvas.style.height !== `${viewport.height}px`) canvas.style.height = `${viewport.height}px`;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      const selection = vectorObjectSelection;
      if (!selection || selection.scope === 'node' || selection.objectIds.length === 0) return;
      const layer = findLayer(document.root, selection.layerId);
      if (!layer || layer.kind !== 'vector' || !layer.visible) return;
      const toDocument = layerDocumentTransform(document.root, layer.id);
      if (!toDocument) return;
      const rings = outlineExtensionSelection(layer, new Set(selection.objectIds));
      if (rings.length === 0) return;
      // Draw in DOCUMENT space; the transform carries zoom, pan and dpr exactly
      // as renderVector composes them.
      context.setTransform(
        scale * viewport.zoom, 0, 0, scale * viewport.zoom,
        scale * viewport.panX, scale * viewport.panY,
      );
      const path = new Path2D();
      for (const ring of rings) {
        ring.forEach(([x, y], index) => {
          const dx = toDocument[0] * x + toDocument[2] * y + toDocument[4];
          const dy = toDocument[1] * x + toDocument[3] * y + toDocument[5];
          if (index === 0) path.moveTo(dx, dy); else path.lineTo(dx, dy);
        });
        path.closePath();
      }
      // Dark halo under a light accent, so the outline reads on pale artwork and
      // on the dark workspace without tinting the artwork itself — colour is
      // identity in this editor and must not be disturbed to show a selection.
      // Widths are divided by zoom so the outline stays constant on screen.
      context.lineJoin = 'round';
      context.strokeStyle = '#07150dd0';
      context.lineWidth = 3 / viewport.zoom;
      context.stroke(path);
      context.strokeStyle = '#84e5ae';
      context.lineWidth = 1.25 / viewport.zoom;
      context.stroke(path);
    };
    if (selectionFrame.current) cancelAnimationFrame(selectionFrame.current);
    selectionFrame.current = requestAnimationFrame(paint);
    return () => {
      if (selectionFrame.current) cancelAnimationFrame(selectionFrame.current);
      selectionFrame.current = 0;
    };
  });

  const screenPoint = (event: { clientX: number; clientY: number }): Point => {
    const bounds = hostRef.current?.getBoundingClientRect();
    return { x: event.clientX - (bounds?.left ?? 0), y: event.clientY - (bounds?.top ?? 0) };
  };

  const documentPoint = (screen: Point): Point => ({ x: (screen.x - view.panX) / view.zoom, y: (screen.y - view.panY) / view.zoom });

  const gridSpacing = (() => {
    let spacing = 50;
    while (spacing * view.zoom < 28) spacing *= 2;
    while (spacing > 25 && spacing * view.zoom > 140) spacing /= 2;
    return spacing;
  })();

  const snapPoint = (point: Point): Point => {
    if (!snapEnabled) return point;
    const tolerance = 8 / view.zoom;
    const nearest = (value: number, axis: 'horizontal' | 'vertical', limit: number) => {
      const guidePositions = (document.guides ?? []).filter((guide) => guide.axis === axis).map((guide) => guide.position);
      const candidates = [0, limit, ...(showGrid ? [Math.round(value / gridSpacing) * gridSpacing] : []), ...guidePositions];
      const candidate = candidates.reduce((best, item) => Math.abs(item - value) < Math.abs(best - value) ? item : best, value);
      return Math.abs(candidate - value) <= tolerance ? candidate : value;
    };
    return {
      x: nearest(point.x, 'vertical', document.width),
      y: nearest(point.y, 'horizontal', document.height),
    };
  };

  const reportPointer = (screen: Point) => {
    const point = documentPoint(screen);
    onPointerReadout({
      x: Math.round(point.x),
      y: Math.round(point.y),
      inside: point.x >= 0 && point.y >= 0 && point.x < document.width && point.y < document.height,
    });
  };

  const zoomAt = (screen: Point, factor: number) => {
    const point = documentPoint(screen);
    const zoom = clampZoom(view.zoom * factor);
    setView({ zoom, panX: screen.x - point.x * zoom, panY: screen.y - point.y * zoom });
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const screen = screenPoint(event);
      if (event.ctrlKey || event.metaKey || event.altKey) zoomAt(screen, Math.exp(-event.deltaY * 0.002));
      else setView((previous) => ({ ...previous, panX: previous.panX - event.deltaX, panY: previous.panY - event.deltaY }));
    };
    host.addEventListener('wheel', wheel, { passive: false });
    return () => host.removeEventListener('wheel', wheel);
  // `zoomAt` closes over the view
  // and is recreated every render; these three are the values the listener actually reads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.zoom, view.panX, view.panY]);

  const transformTarget = (layer: Layer, object?: ExtensionObjectTarget | InteractionTarget | null): CanvasTransformTarget | null => {
    const parent = layerParentTransform(document.root, layer.id) ?? IDENTITY_MATRIX;
    if (object) return { layer, object, bounds: object.bounds, transform: object.transform, parentTransform: multiplyMatrix(parent, layer.transform) };
    const bounds = layerBounds(layer);
    return bounds ? { layer, bounds, transform: layer.transform, parentTransform: parent } : null;
  };

  const selectedTransformTarget = (): CanvasTransformTarget | null => {
    const layerId = document.activeLayerIds[0];
    const layer = layerId ? findLayer(document.root, layerId) : undefined;
    if (!layer) return null;
    const selectedIds = vectorObjectSelection?.layerId === layer.id ? vectorObjectSelection.objectIds : [];
    if (selectedIds.length > 1 && layer.kind === 'vector') {
      const layerTransform = layerDocumentTransform(document.root, layer.id);
      if (!layerTransform) return null;
      const bounds = selectedIds.flatMap((id) => {
        const object = getExtensionObject(layer, id);
        return object ? [transformRectBoundsPrecise(multiplyMatrix(layerTransform, object.transform), object.bounds)] : [];
      });
      if (bounds.length === 0) return null;
      const left = Math.min(...bounds.map((rect) => rect.x));
      const top = Math.min(...bounds.map((rect) => rect.y));
      const right = Math.max(...bounds.map((rect) => rect.x + rect.width));
      const bottom = Math.max(...bounds.map((rect) => rect.y + rect.height));
      return { layer, bounds: { x: left, y: top, width: right - left, height: bottom - top }, transform: IDENTITY_MATRIX, parentTransform: IDENTITY_MATRIX, collectiveObjectIds: selectedIds };
    }
    const objectId = selectedIds[0];
    return transformTarget(layer, objectId ? getExtensionObject(layer, objectId) : null);
  };

  const EMPTY_DETAIL_IDS: ReadonlySet<string> = new Set();

  const hitTransformTargets = (point: Point): CanvasTransformTarget[] => {
    const targets: CanvasTransformTarget[] = [];
    for (const layer of findLayersAtPoint(document.root, point).filter((candidate) => layerInIsolation(document, candidate.id, isolationScope))) {
      if (layer.kind === 'vector') {
        const parent = layerParentTransform(document.root, layer.id) ?? IDENTITY_MATRIX;
        // Only the objects already in node scope can contribute node or segment
        // targets — the filter below discards the rest — so do not pay to
        // compute them for every path in the layer.
        const detailObjectIds = vectorObjectSelection?.scope === 'node' && vectorObjectSelection.layerId === layer.id
          ? new Set(vectorObjectSelection.objectIds)
          : EMPTY_DETAIL_IDS;
        const candidates = hitTestExtensionTargets(layer, point, view.zoom, parent, detailObjectIds).filter((candidate) => (candidate.scope === 'object' || (vectorObjectSelection?.scope === 'node' && vectorObjectSelection.layerId === layer.id && vectorObjectSelection.objectIds.includes(candidate.id)))
          && (!isolationScope?.objectId || isolationScope.layerId !== layer.id || candidate.id === isolationScope.objectId));
        for (const object of candidates) {
          const target = transformTarget(layer, object);
          if (target) targets.push(target);
        }
        continue;
      }
      const target = transformTarget(layer);
      if (target) targets.push(target);
    }
    return targets;
  };

  const hitTransformTarget = (point: Point): CanvasTransformTarget | null => {
    const targets = hitTransformTargets(point);
    if (targets.length === 0) return null;
    const signature = targets.map((target) => `${target.layer.id}:${target.object?.id ?? 'layer'}`).join('|');
    const previous = hitCycle.current;
    const repeated = previous && previous.signature === signature && performance.now() - previous.at < 800 && Math.hypot(previous.point.x - point.x, previous.point.y - point.y) < 6 / view.zoom;
    const index = repeated ? (previous.index + 1) % targets.length : 0;
    hitCycle.current = { signature, index, at: performance.now(), point };
    return targets[index] ?? targets[0] ?? null;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.button !== 1) return;
    lastInputAt.current = performance.now();
    const screen = screenPoint(event);
    const rawPoint = documentPoint(screen);
    const activeTool: ToolId = temporaryHand || event.button === 1 ? 'hand' : tool;
    const point = activeTool === 'marquee' || activeTool === 'move' ? snapPoint(rawPoint) : rawPoint;
    reportPointer(screen);
    if (toolBlockedReason && activeTool === tool) return;
    if (activeTool === 'zoom') {
      zoomAt(screen, event.altKey ? 0.8 : 1.25);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    if (activeTool === 'hand') {
      interaction.current = { kind: 'pan', startScreen: screen, startDocument: point };
      return;
    }
    if (activeTool === 'note') {
      if (point.x >= 0 && point.y >= 0 && point.x <= document.width && point.y <= document.height) onCreateNote(point);
      return;
    }
    if (activeTool === 'marquee') {
      interaction.current = { kind: 'select', startScreen: screen, startDocument: point };
      setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
      return;
    }
    if (activeTool === 'shape') {
      interaction.current = { kind: 'shape', startScreen: screen, startDocument: rawPoint };
      setShapePreview({ kind: shapeKind, rect: { x: rawPoint.x, y: rawPoint.y, width: 0, height: 0 } });
      return;
    }
    if (activeTool === 'pen') {
      interaction.current = { kind: 'pen', startScreen: screen, startDocument: rawPoint };
      setPenPointer(rawPoint);
      return;
    }
    if (activeTool === 'shaper') {
      interaction.current = { kind: 'shaper', startScreen: screen, startDocument: rawPoint };
      setShaperPoints([rawPoint]);
      return;
    }
    if (activeTool === 'knife') {
      const layerId = vectorObjectSelection?.layerId ?? document.activeLayerIds[0];
      const layer = layerId ? findLayer(document.root, layerId) : undefined;
      if (layer?.kind !== 'vector') return;
      interaction.current = { kind: 'knife', startScreen: screen, startDocument: rawPoint, targetLayerId: layer.id };
      setKnifePreview({ start: rawPoint, end: rawPoint });
      return;
    }
    if (quickMaskActive && (activeTool === 'brush' || activeTool === 'eraser')) {
      const settings = { ...brush, mode: 'paint' as const };
      const firstPoint = { ...point, pressure: event.pointerType === 'pen' ? Math.max(0, Math.min(1, event.pressure)) : 1 };
      history.begin(document, new QuickMaskStrokeCommand(settings, firstPoint, activeTool === 'brush' ? 'add' : 'subtract'));
      interaction.current = { kind: 'paint', startScreen: screen, startDocument: point };
      return;
    }
    const activeLayer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (activeTool === 'quick-selection' && activeLayer?.kind === 'raster') {
      const command = new QuickSelectionCommand(activeLayer.id, point, brush.size / 2, quickSelectionTolerance, selectionMode);
      const result = history.begin(document, command);
      if (result.outcome === 'changed') interaction.current = { kind: 'paint', startScreen: screen, startDocument: point };
      return;
    }
    if ((activeTool === 'dodge' || activeTool === 'burn') && activeLayer?.kind === 'raster') {
      const firstPoint = { ...point, pressure: event.pointerType === 'pen' ? Math.max(0, Math.min(1, event.pressure)) : 1 };
      const command = new ExposureStrokeCommand(activeLayer.id, activeTool, brush, exposureSettings, firstPoint);
      const result = history.begin(document, command);
      if (result.outcome === 'changed') interaction.current = { kind: 'paint', startScreen: screen, startDocument: point };
      return;
    }
    if (activeTool === 'move') {
      const requestedHandle = event.target instanceof Element
        ? event.target.closest('[data-transform-handle]')?.getAttribute('data-transform-handle') ?? null
        : null;
      const selectedTarget = selectedTransformTarget();
      if (selectedTarget && !selectedTarget.layer.locked && !selectedTarget.layer.positionLocked && (requestedHandle === 'rotate' || isResizeHandle(requestedHandle))) {
        // Alt-drag on a text layer's handles reflows the layout box (glyphs
        // keep their size); plain drag keeps the natural glyph scaling.
        if (event.altKey && selectedTarget.layer.kind === 'text' && selectedTarget.layer.text.schemaVersion === 2) return;
        const textBoxLayer = event.altKey && isResizeHandle(requestedHandle) && selectedTarget.layer.kind === 'text' && !selectedTarget.object
          ? selectedTarget.layer
          : null;
        const command = textBoxLayer
          ? createExtensionTextEditCommand(textBoxLayer)
          : requestedHandle !== 'rotate' && !scaleStrokesEffects && selectedTarget.object
          ? createExtensionScaleCommand(selectedTarget.layer, selectedTarget.object.id) ?? targetCommand(selectedTarget)
          : requestedHandle !== 'rotate' && !scaleStrokesEffects && selectedTarget.collectiveObjectIds
          ? createExtensionMultiScaleCommand(selectedTarget.layer, selectedTarget.collectiveObjectIds) ?? targetCommand(selectedTarget)
          : targetCommand(selectedTarget);
        if (!command) return;
        const effectiveTransform = multiplyMatrix(selectedTarget.parentTransform, selectedTarget.transform);
        history.begin(document, command);
        interaction.current = {
          kind: requestedHandle === 'rotate' ? 'rotate' : 'resize',
          startScreen: screen,
          startDocument: rawPoint,
          originalTransform: selectedTarget.transform,
          parentTransform: selectedTarget.parentTransform,
          transformBounds: selectedTarget.bounds,
          targetLayerId: selectedTarget.layer.id,
          ...(selectedTarget.object ? { targetObjectId: selectedTarget.object.id } : {}),
          ...(textBoxLayer ? { textBoxOriginal: { ...textBoxLayer.text.box } } : {}),
          ...(requestedHandle === 'rotate' ? {
            transformCenter: transformPoint(effectiveTransform, {
              x: selectedTarget.bounds.x + selectedTarget.bounds.width / 2,
              y: selectedTarget.bounds.y + selectedTarget.bounds.height / 2,
            }),
          } : { resizeHandle: requestedHandle }),
        };
        return;
      }
      // Cmd/Ctrl forces a rubber band from anywhere, over anything.
      //
      // Without it the marquee is unreachable on exactly the artwork it exists
      // for: a colour trace is a partition, so every interior point hits an
      // object and the band could only ever be started off-canvas. Cmd is the
      // only free modifier on a Move press — Shift toggles objects, Alt both
      // reflows text on resize and suppresses snapping mid-drag, Space and the
      // middle button are Hand.
      if (event.metaKey || event.ctrlKey) {
        const bandLayer = activeLayer?.kind === 'vector' ? activeLayer : undefined;
        if (bandLayer) {
          interaction.current = {
            kind: 'object-band',
            startScreen: screen,
            startDocument: rawPoint,
            targetLayerId: bandLayer.id,
            bandBaseObjectIds: event.shiftKey && vectorObjectSelection?.layerId === bandLayer.id ? [...vectorObjectSelection.objectIds] : [],
          };
          setMarquee({ x: rawPoint.x, y: rawPoint.y, width: 0, height: 0 });
          return;
        }
      }
      // Clicks on the backdrop (outside the document) never hit content —
      // even when an oversized layer extends under the pointer, it is
      // clipped from view there. Both backdrop clicks and clicks on empty
      // canvas deselect, matching every other editor.
      const insideDocument = rawPoint.x >= 0 && rawPoint.y >= 0 && rawPoint.x < document.width && rawPoint.y < document.height;
      let hitTarget = insideDocument ? hitTransformTarget(rawPoint) : null;
      if (!hitTarget) {
        if (document.activeLayerIds.length > 0) history.applyTransient(document, new SetActiveLayersCommand([]));
        onVectorObjectSelection(null);
        return;
      }
      if (activeLayer?.id !== hitTarget.layer.id) history.applyTransient(document, new SetActiveLayersCommand([hitTarget.layer.id]));
      if (hitTarget.object) {
        const hitObjectId = hitTarget.object.id;
        const pathNodeId = 'scope' in hitTarget.object && hitTarget.object.scope === 'path-node' ? hitTarget.object.detailId : undefined;
        const pathSegmentId = 'scope' in hitTarget.object && hitTarget.object.scope === 'path-segment' ? hitTarget.object.detailId : undefined;
        if (pathNodeId) {
          const currentNodes = vectorObjectSelection?.scope === 'node' ? vectorObjectSelection.nodeIds ?? [] : [];
          const nodeIds = event.shiftKey ? currentNodes.includes(pathNodeId) ? currentNodes.filter((id) => id !== pathNodeId) : [...currentNodes, pathNodeId] : [pathNodeId];
          onVectorObjectSelection({ layerId: hitTarget.layer.id, objectIds: [hitObjectId], scope: 'node', nodeIds });
          if (event.shiftKey) return;
        }
        const currentIds = vectorObjectSelection?.layerId === hitTarget.layer.id ? vectorObjectSelection.objectIds : [];
        const preserveCollective = !event.shiftKey && currentIds.length > 1 && currentIds.includes(hitObjectId);
        // Clicking a member selects its whole group — that is the point of
        // having recorded one — unless you have entered the group, in which case
        // you are working on its parts.
        const hitVectorLayer = hitTarget.layer.kind === 'vector' ? hitTarget.layer : null;
        const insideThisGroup = Boolean(hitVectorLayer
          && groupEntry?.layerId === hitVectorLayer.id
          && groupEntry.groupId === groupIdOf(hitVectorLayer.vector.objects.find((object) => object.id === hitObjectId)));
        const expand = (ids: string[]) => (hitVectorLayer && !insideThisGroup ? expandToGroups(hitVectorLayer, ids) : ids);
        const objectIds = event.shiftKey
          ? currentIds.includes(hitObjectId) ? currentIds.filter((id) => id !== hitObjectId) : [...currentIds, ...expand([hitObjectId])]
          : preserveCollective ? currentIds : expand([hitObjectId]);
        if (pathSegmentId) onVectorObjectSelection({ layerId: hitTarget.layer.id, objectIds: [hitObjectId], scope: 'node', nodeIds: [] });
        else if (!pathNodeId) onVectorObjectSelection(objectIds.length ? { layerId: hitTarget.layer.id, objectIds, scope: 'object' } : null);
        if (event.shiftKey) return;
        if (preserveCollective) hitTarget = selectedTransformTarget() ?? hitTarget;
      } else {
        onVectorObjectSelection(null);
      }
      if (hitTarget.layer.locked || hitTarget.layer.positionLocked) return;
      const command = targetCommand(hitTarget);
      if (!command) return;
      history.begin(document, command);
      interaction.current = {
        kind: 'move',
        startScreen: screen,
        startDocument: rawPoint,
        originalTransform: hitTarget.transform,
        parentTransform: hitTarget.parentTransform,
        transformBounds: hitTarget.bounds,
        targetLayerId: hitTarget.layer.id,
        ...(hitTarget.object ? { targetObjectId: hitTarget.object.id } : {}),
      };
      return;
    }
    if (activeTool === 'gradient' && activeLayer?.kind === 'raster' && !maskEditingLayerId && !activeLayer.locked && !activeLayer.pixelsLocked) {
      interaction.current = { kind: 'gradient', startScreen: screen, startDocument: point };
      setGradient({ start: point, end: point });
      return;
    }
    if ((activeTool === 'clone-stamp' || activeTool === 'healing-brush') && activeLayer?.kind === 'raster') {
      const effective = layerDocumentTransform(document.root, activeLayer.id);
      const inverse = effective ? invertMatrix(effective) : null;
      if (!inverse) return;
      const localPoint = transformPoint(inverse, point);
      if (activeTool === 'clone-stamp' && event.altKey) {
        setCloneSource({ layerId: activeLayer.id, point: localPoint });
        setCloneOffset(null);
        return;
      }
      if (activeTool === 'clone-stamp' && cloneSource?.layerId !== activeLayer.id) return;
      const usePerspectiveClone = activeTool === 'clone-stamp' && perspectiveClone && Boolean(perspectivePlanes[0]);
      const offset = activeTool === 'clone-stamp'
        ? usePerspectiveClone ? { x: 0, y: 0 } : cloneOffset ?? { x: cloneSource!.point.x - localPoint.x, y: cloneSource!.point.y - localPoint.y }
        : { x: localPoint.x + brush.size * 2 < activeLayer.raster.width ? brush.size * 2 : -brush.size * 2, y: 0 };
      if (activeTool === 'clone-stamp' && !usePerspectiveClone && !cloneOffset) setCloneOffset(offset);
      const settings = { ...brush, mode: 'paint' as const };
      const firstPoint = { ...point, pressure: event.pointerType === 'pen' ? Math.max(0, Math.min(1, event.pressure)) : 1 };
      const snapshot = activeLayer.raster.clone();
      const localPlane = usePerspectiveClone
        ? perspectivePlanes[0]!.corners.map((corner) => transformPoint(inverse, corner)) as unknown as Quad
        : null;
      const command = activeTool === 'clone-stamp'
        ? localPlane
          ? new PerspectiveCloneStampCommand(activeLayer.id, settings, firstPoint, snapshot, localPlane, cloneSource!.point, localPoint)
          : new CloneStampCommand(activeLayer.id, settings, firstPoint, snapshot, offset)
        : new HealStrokeCommand(activeLayer.id, settings, firstPoint, snapshot, offset);
      history.begin(document, command);
      interaction.current = { kind: 'paint', startScreen: screen, startDocument: point };
      return;
    }
    if ((activeTool === 'brush' || activeTool === 'history-brush' || activeTool === 'eraser') && activeLayer?.kind === 'raster') {
      const settings = { ...brush, mode: activeTool === 'eraser' ? 'erase' as const : 'paint' as const };
      const firstPoint = { ...point, pressure: event.pointerType === 'pen' ? Math.max(0, Math.min(1, event.pressure)) : 1 };
      const command = activeTool === 'history-brush' && historyBrushSource
        ? new HistoryBrushStrokeCommand(activeLayer.id, settings, firstPoint, historyBrushSource)
        : maskEditingLayerId === activeLayer.id && activeLayer.mask
        ? new PaintMaskStrokeCommand(activeLayer.id, activeLayer.mask.id, settings, firstPoint)
        : new PaintStrokeCommand(activeLayer.id, settings, firstPoint);
      history.begin(document, command);
      interaction.current = { kind: 'paint', startScreen: screen, startDocument: point };
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const screen = screenPoint(event);
    setCursor(screen);
    reportPointer(screen);
    const current = interaction.current;
    if (!current) return;
    lastInputAt.current = performance.now();
    const rawPoint = documentPoint(screen);
    const point = current.kind === 'select' ? snapPoint(rawPoint) : rawPoint;
    if (current.kind === 'pan') {
      setView((previous) => ({ ...previous, panX: previous.panX + event.movementX, panY: previous.panY + event.movementY }));
      return;
    }
    if (current.kind === 'object-band') {
      // Unsnapped deliberately: snapPoint moves an edge by up to 8/zoom document
      // units, and band membership here is a sub-pixel question.
      setMarquee({ x: current.startDocument.x, y: current.startDocument.y, width: rawPoint.x - current.startDocument.x, height: rawPoint.y - current.startDocument.y });
      return;
    }
    if (current.kind === 'select') {
      setMarquee({ x: current.startDocument.x, y: current.startDocument.y, width: point.x - current.startDocument.x, height: point.y - current.startDocument.y });
      return;
    }
    if (current.kind === 'shape') {
      setShapePreview({ kind: shapeKind, rect: shapeRect(current.startDocument, rawPoint, event.shiftKey, event.altKey) });
      return;
    }
    if (current.kind === 'pen') {
      setPenPointer(rawPoint);
      return;
    }
    if (current.kind === 'shaper') {
      setShaperPoints((points) => {
        const last = points.at(-1);
        return points.length >= 20_000 || (last && Math.hypot(last.x - rawPoint.x, last.y - rawPoint.y) < 1 / view.zoom) ? points : [...points, rawPoint];
      });
      return;
    }
    if (current.kind === 'knife') {
      setKnifePreview({ start: current.startDocument, end: rawPoint });
      return;
    }
    if (current.kind === 'gradient') {
      setGradient({ start: current.startDocument, end: rawPoint });
      return;
    }
    if (current.kind === 'paint') {
      history.updateLive(document, (command) => {
        const samples = event.nativeEvent.getCoalescedEvents?.() ?? [event.nativeEvent];
        for (const sample of samples) {
          const samplePoint = documentPoint(screenPoint(sample));
          const pressure = sample.pointerType === 'pen' ? Math.max(0, Math.min(1, sample.pressure)) : 1;
          if (command instanceof PaintStrokeCommand) command.append(document, { ...samplePoint, pressure });
          else if (command instanceof PaintMaskStrokeCommand) command.append(document, { ...samplePoint, pressure });
          else if (command instanceof QuickMaskStrokeCommand) command.append(document, { ...samplePoint, pressure });
          else if (command instanceof QuickSelectionCommand) command.append(document, samplePoint);
          else if (command instanceof ExposureStrokeCommand) command.append(document, { ...samplePoint, pressure });
        }
      });
      return;
    }
    if (current.kind === 'resize' && current.originalTransform && current.transformBounds && current.resizeHandle) {
      const parent = current.parentTransform ?? IDENTITY_MATRIX;
      const inverse = invertMatrix(multiplyMatrix(parent, current.originalTransform));
      if (!inverse) return;
      const localPoint = transformPoint(inverse, rawPoint);
      const start = handlePoint(current.transformBounds, current.resizeHandle);
      const anchor = oppositeHandlePoint(current.transformBounds, current.resizeHandle);
      let scaleX = current.resizeHandle === 'n' || current.resizeHandle === 's' ? 1 : nonZeroScale((localPoint.x - anchor.x) / (start.x - anchor.x));
      let scaleY = current.resizeHandle === 'e' || current.resizeHandle === 'w' ? 1 : nonZeroScale((localPoint.y - anchor.y) / (start.y - anchor.y));
      if (current.textBoxOriginal) {
        // Alt-resize: reflow the layout box instead of scaling glyphs. The
        // box anchors at its top-left; vertical drags pin the height.
        const boxOriginal = current.textBoxOriginal;
        const width = Math.max(20, Math.round(boxOriginal.width * Math.abs(scaleX)));
        const height = Math.max(24, Math.round(boxOriginal.height * Math.abs(scaleY)));
        const vertical = current.resizeHandle.includes('n') || current.resizeHandle.includes('s');
        current.changed = true;
        history.updateLive(document, (command) => {
          if (!isTextEditCommand(command)) return;
          (command as unknown as TextPatchCommand).update(document, (text) => ({
            ...text,
            box: { width, height: vertical ? height : text.box.height, autoHeight: vertical ? false : boxOriginal.autoHeight },
          }));
        });
        return;
      }
      const corner = current.resizeHandle.length === 2;
      if (corner && !event.shiftKey) {
        const uniform = Math.abs(scaleX - 1) >= Math.abs(scaleY - 1) ? scaleX : scaleY;
        scaleX = nonZeroScale(uniform);
        scaleY = nonZeroScale(uniform);
      }
      const next = multiplyMatrix(current.originalTransform, matrixAround(anchor, [scaleX, 0, 0, scaleY, 0, 0]));
      current.changed = true;
      history.updateLive(document, (command) => {
        if (isMatrixTransformCommand(command)) command.update(document, next);
      });
      return;
    }
    if (current.kind === 'rotate' && current.originalTransform && current.transformCenter) {
      const centerScreen = {
        x: view.panX + current.transformCenter.x * view.zoom,
        y: view.panY + current.transformCenter.y * view.zoom,
      };
      const startAngle = Math.atan2(current.startScreen.y - centerScreen.y, current.startScreen.x - centerScreen.x);
      const currentAngle = Math.atan2(screen.y - centerScreen.y, screen.x - centerScreen.x);
      let delta = currentAngle - startAngle;
      if (event.shiftKey) delta = Math.round(delta / (Math.PI / 12)) * (Math.PI / 12);
      const cosine = Math.cos(delta);
      const sine = Math.sin(delta);
      const parent = current.parentTransform ?? IDENTITY_MATRIX;
      const inverseParent = invertMatrix(parent);
      if (!inverseParent) return;
      const effective = multiplyMatrix(parent, current.originalTransform);
      const next = multiplyMatrix(inverseParent, multiplyMatrix(matrixAround(current.transformCenter, [cosine, sine, -sine, cosine, 0, 0]), effective));
      current.changed = true;
      history.updateLive(document, (command) => {
        if (isMatrixTransformCommand(command)) command.update(document, next);
      });
      return;
    }
    if (current.kind === 'move' && current.originalTransform) {
      const parent = current.parentTransform ?? IDENTITY_MATRIX;
      const inverseParent = invertMatrix(parent);
      if (!inverseParent) return;
      const start = transformPoint(inverseParent, current.startDocument);
      const position = transformPoint(inverseParent, rawPoint);
      const deltaX = position.x - start.x;
      const deltaY = position.y - start.y;
      const original = current.originalTransform;
      let next: Matrix = [original[0], original[1], original[2], original[3], original[4] + deltaX, original[5] + deltaY];
      if (snapEnabled && !event.altKey && current.transformBounds) {
        const visibleBounds = transformRectBoundsPrecise(multiplyMatrix(parent, next), current.transformBounds);
        const verticalGuides = (document.guides ?? []).filter((guide) => guide.axis === 'vertical').map((guide) => guide.position);
        const horizontalGuides = (document.guides ?? []).filter((guide) => guide.axis === 'horizontal').map((guide) => guide.position);
        const xValues = [visibleBounds.x, visibleBounds.x + visibleBounds.width / 2, visibleBounds.x + visibleBounds.width];
        const yValues = [visibleBounds.y, visibleBounds.y + visibleBounds.height / 2, visibleBounds.y + visibleBounds.height];
        const otherTargets = spatialSnapTargets.filter((target) => target.layerId !== current.targetLayerId || target.objectId !== current.targetObjectId);
        const objectX = otherTargets.flatMap((target) => [target.bounds.x, target.bounds.x + target.bounds.width / 2, target.bounds.x + target.bounds.width]);
        const objectY = otherTargets.flatMap((target) => [target.bounds.y, target.bounds.y + target.bounds.height / 2, target.bounds.y + target.bounds.height]);
        const snapped = snapRectTranslation(
          visibleBounds,
          [0, document.width / 2, document.width, ...verticalGuides, ...objectX, ...(showGrid ? xValues.map((value) => Math.round(value / gridSpacing) * gridSpacing) : [])],
          [0, document.height / 2, document.height, ...horizontalGuides, ...objectY, ...(showGrid ? yValues.map((value) => Math.round(value / gridSpacing) * gridSpacing) : [])],
          8 / view.zoom,
        );
        next = [
          next[0], next[1], next[2], next[3],
          next[4] + inverseParent[0] * snapped.offset.x + inverseParent[2] * snapped.offset.y,
          next[5] + inverseParent[1] * snapped.offset.x + inverseParent[3] * snapped.offset.y,
        ];
        const labels: string[] = [];
        if (snapped.vertical !== undefined) {
          const relation = objectX.includes(snapped.vertical) ? 'Object edge / center' : verticalGuides.includes(snapped.vertical) ? 'Guide' : showGrid && snapped.vertical % gridSpacing === 0 ? 'Grid' : 'Canvas';
          labels.push(`${relation} · Δx ${snapped.offset.x.toFixed(2)} px`);
        }
        if (snapped.horizontal !== undefined) {
          const relation = objectY.includes(snapped.horizontal) ? 'Object edge / center' : horizontalGuides.includes(snapped.horizontal) ? 'Guide' : showGrid && snapped.horizontal % gridSpacing === 0 ? 'Grid' : 'Canvas';
          labels.push(`${relation} · Δy ${snapped.offset.y.toFixed(2)} px`);
        }
        setSmartGuides(snapped.vertical === undefined && snapped.horizontal === undefined ? null : { ...(snapped.vertical === undefined ? {} : { vertical: snapped.vertical }), ...(snapped.horizontal === undefined ? {} : { horizontal: snapped.horizontal }), labels });
      } else setSmartGuides(null);
      current.changed = Math.hypot(screen.x - current.startScreen.x, screen.y - current.startScreen.y) > 1;
      history.updateLive(document, (command) => {
        if (isMatrixTransformCommand(command)) command.update(document, next);
      });
    }
  };

  const finishInteraction = (event: ReactPointerEvent<HTMLDivElement>, cancelled = false) => {
    const current = interaction.current;
    if (!current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (current.kind === 'paint') {
      if (cancelled) history.cancelLive(document);
      else history.commitLive(document);
    }
    if (current.kind === 'move' || current.kind === 'resize' || current.kind === 'rotate') {
      if (cancelled || !current.changed) history.cancelLive(document);
      else history.commitLive(document);
    }
    if (current.kind === 'select' && !cancelled) {
      const endScreen = screenPoint(event);
      const endDocument = snapPoint(documentPoint(endScreen));
      const dragged = Math.hypot(endScreen.x - current.startScreen.x, endScreen.y - current.startScreen.y) > 3;
      if (dragged) {
        history.execute(document, SelectionCommand.rectangle({
          x: current.startDocument.x,
          y: current.startDocument.y,
          width: endDocument.x - current.startDocument.x,
          height: endDocument.y - current.startDocument.y,
        }, selectionMode));
      } else if (selectionMode === 'replace' && (document.selection.inverted || document.selection.regions.length > 0)) {
        history.execute(document, SelectionCommand.clear());
      }
    }
    if (current.kind === 'object-band' && !cancelled) {
      const endScreen = screenPoint(event);
      const endDocument = documentPoint(endScreen);
      const dragged = Math.hypot(endScreen.x - current.startScreen.x, endScreen.y - current.startScreen.y) > 3;
      const layer = current.targetLayerId ? findLayer(document.root, current.targetLayerId) : undefined;
      if (dragged && layer?.kind === 'vector') {
        const toDocument = layerDocumentTransform(document.root, layer.id);
        const toLayer = toDocument ? invertMatrix(toDocument) : null;
        if (toLayer) {
          const corners = [
            transformPoint(toLayer, current.startDocument),
            transformPoint(toLayer, { x: endDocument.x, y: current.startDocument.y }),
            transformPoint(toLayer, endDocument),
            transformPoint(toLayer, { x: current.startDocument.x, y: endDocument.y }),
          ];
          const left = Math.min(...corners.map((corner) => corner.x));
          const top = Math.min(...corners.map((corner) => corner.y));
          const band = {
            x: left,
            y: top,
            width: Math.max(...corners.map((corner) => corner.x)) - left,
            height: Math.max(...corners.map((corner) => corner.y)) - top,
          };
          // Contained, not crossing. On a partition the backdrop's bounding box
          // IS the document, so a crossing band takes it every time — measured
          // on the reference illustration, a band around one figure took 75
          // objects including the backdrop crossing, and 61 with none of it
          // contained.
          const inside = selectExtensionWithinRect(layer, band);
          const objectIds = [...new Set([...(current.bandBaseObjectIds ?? []), ...inside])];
          if (objectIds.length > 0) onVectorObjectSelection({ layerId: layer.id, objectIds, scope: 'object' });
          else onVectorObjectSelection(null);
        }
      } else if (!dragged) {
        // The press-time clear used to fire here on every stray click, as a
        // transient history entry, before any drag could begin — which would
        // have destroyed the band's own target layer.
        if (document.activeLayerIds.length > 0) history.applyTransient(document, new SetActiveLayersCommand([]));
        onVectorObjectSelection(null);
      }
    }
    if (current.kind === 'shape' && !cancelled) {
      const endScreen = screenPoint(event);
      const endDocument = documentPoint(endScreen);
      const dragged = Math.hypot(endScreen.x - current.startScreen.x, endScreen.y - current.startScreen.y) > 3;
      const rect = dragged
        ? shapeRect(current.startDocument, endDocument, event.shiftKey, event.altKey)
        : { x: current.startDocument.x - 80, y: current.startDocument.y - 60, width: 160, height: 120 };
      void Promise.all([import('../vector/index'), import('../vector/grid-tools')]).then(([vector, grids]) => {
        if (!mounted.current) return;
        vector.activateVectorSubsystem();
        const layer = shapeKind === 'rectangular-grid' || shapeKind === 'polar-grid'
          ? grids.createGridLayer(shapeKind, Math.max(1, rect.width), Math.max(1, rect.height), gridSettings)
          : vector.createShapeLayer(shapeKind);
        const placement = vectorDrawPlacement(document, vectorDrawMode);
        layer.clipping = placement.clipping;
        layer.transform = transformForPlacement([1, 0, 0, 1, rect.x, rect.y], placement);
        const object = layer.vector.objects[0];
        if (shapeKind === 'rectangle' && object?.type === 'rectangle') {
          const maximum = Math.min(rect.width / 2, rect.height / 2);
          object.geometry = { ...object.geometry, x: 0, y: 0, width: Math.max(1, rect.width), height: Math.max(1, rect.height), radius: Object.fromEntries(Object.entries(object.geometry.radius).map(([key, value]) => [key, Math.min(value, maximum)])) as typeof object.geometry.radius };
        }
        else if (shapeKind === 'ellipse' && object?.type === 'ellipse') object.geometry = { x: 0, y: 0, width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
        history.execute(document, new AddLayerCommand(layer, placement.parentId, placement.index));
        onVectorObjectSelection(object ? { layerId: layer.id, objectIds: [object.id] } : null);
      });
    }
    if (current.kind === 'pen' && !cancelled) {
      const end = documentPoint(screenPoint(event));
      const dx = end.x - current.startDocument.x;
      const dy = end.y - current.startDocument.y;
      const smooth = Math.hypot(dx, dy) > 3 / view.zoom;
      setPenDraft((nodes) => [...nodes, {
        anchor: current.startDocument,
        incoming: smooth ? { x: current.startDocument.x - dx, y: current.startDocument.y - dy } : null,
        outgoing: smooth ? { x: current.startDocument.x + dx, y: current.startDocument.y + dy } : null,
        continuity: smooth ? 'smooth' : 'corner',
      }]);
      setPenPointer(null);
    }
    if (current.kind === 'shaper' && !cancelled) {
      const end = documentPoint(screenPoint(event));
      const gesture = [...shaperPoints, end];
      void Promise.all([import('../vector/index'), import('../vector/shaper')]).then(([vector, shaper]) => {
        if (!mounted.current) return;
        const recognition = shaper.recognizeShaperGesture(gesture);
        if (!recognition) return;
        vector.activateVectorSubsystem();
        const layer = shaper.createShaperLayer(recognition);
        const placement = vectorDrawPlacement(document, vectorDrawMode);
        layer.clipping = placement.clipping;
        layer.transform = transformForPlacement(layer.transform, placement);
        history.execute(document, new AddLayerCommand(layer, placement.parentId, placement.index));
        const object = layer.vector.objects[0];
        onVectorObjectSelection(object ? { layerId: layer.id, objectIds: [object.id], scope: 'object' } : null);
      });
    }
    if (current.kind === 'knife' && !cancelled && current.targetLayerId) {
      const end = documentPoint(screenPoint(event));
      if (Math.hypot(end.x - current.startDocument.x, end.y - current.startDocument.y) > 3 / view.zoom) {
        const layer = findLayer(document.root, current.targetLayerId);
        const layerTransform = layer?.kind === 'vector' ? layerDocumentTransform(document.root, layer.id) : null;
        const inverse = layerTransform ? invertMatrix(layerTransform) : null;
        if (layer?.kind === 'vector' && inverse) {
          const selectedIds = vectorObjectSelection?.layerId === layer.id && vectorObjectSelection.objectIds.length
            ? vectorObjectSelection.objectIds
            : layer.vector.objects.map((object) => object.id);
          const localStart = transformPoint(inverse, current.startDocument);
          const localEnd = transformPoint(inverse, end);
          void Promise.all([import('../vector/scissors-knife'), import('../vector/runtime')]).then(([knife, runtime]) => {
            if (!mounted.current) return;
            history.execute(document, new runtime.UpdateVectorSceneCommand(layer.id, 'Knife cut', (objects) => knife.knifeCutObjects(objects, selectedIds, localStart, localEnd)));
          });
        }
      }
    }
    if (current.kind === 'gradient' && gradient && !cancelled) {
      const activeLayer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
      if (activeLayer?.kind === 'raster') {
        const parts = backgroundColor.match(/[a-f\d]{2}/gi)?.map((value) => Number.parseInt(value, 16)) ?? [255, 255, 255];
        history.execute(document, new FillRasterCommand(activeLayer.id, {
          type: 'linear-gradient',
          start: gradient.start,
          end: gradient.end,
          startColor: brush.color,
          endColor: [parts[0] ?? 255, parts[1] ?? 255, parts[2] ?? 255, 255],
        }));
      }
    }
    interaction.current = null;
    setMarquee(null);
    setGradient(null);
    setShapePreview(null);
    setKnifePreview(null);
    setShaperPoints([]);
    setSmartGuides(null);
  };

  const beginTextEditing = (layer: TextLayer, point: Point | null = null) => {
    if (layer.text.schemaVersion === 2) { onEditPortableText(layer, point); return; }
    const start = () => {
      const command = createExtensionTextEditCommand(layer);
      if (!command) return;
      if (!document.activeLayerIds.includes(layer.id)) history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
      history.begin(document, command);
      setTextEditor({ layerId: layer.id, value: layer.text.value });
    };
    void import('../vector/index').then((vector) => { if (!mounted.current) return; vector.activateVectorSubsystem(); start(); });
  };

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if ((temporaryHand ? 'hand' : tool) !== 'move' || textEditor) return;
    const point = documentPoint(screenPoint(event));
    const textLayer = findLayersAtPoint(document.root, point).filter((layer) => layerInIsolation(document, layer.id, isolationScope)).find((layer): layer is TextLayer => layer.kind === 'text');
    if (textLayer) {
      beginTextEditing(textLayer, point);
      return;
    }
    for (const layer of findLayersAtPoint(document.root, point).filter((candidate) => layerInIsolation(document, candidate.id, isolationScope))) {
      if (layer.kind !== 'vector') continue;
      const parent = layerParentTransform(document.root, layer.id) ?? IDENTITY_MATRIX;
      const target = hitTestExtensionTargets(layer, point, view.zoom, parent).find((candidate) => candidate.scope === 'object');
      const object = target ? layer.vector.objects.find((candidate) => candidate.id === target.id) : undefined;
      // Double-click a grouped object: step INSIDE the group first, so the next
      // click selects one member. Not isolation — that hides and dims the rest
      // of the artwork, and you group parts of a figure to edit them in context.
      const enteredGroupId = object ? groupIdOf(object) : null;
      if (object && enteredGroupId && groupEntry?.groupId !== enteredGroupId) {
        history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
        onGroupEntry({ layerId: layer.id, groupId: enteredGroupId });
        onVectorObjectSelection({ layerId: layer.id, objectIds: [object.id], scope: 'object' });
        return;
      }
      // Already isolated on an object: a double-click that lands anywhere OTHER
      // than that object steps OUT, rather than isolating on whatever was under
      // the cursor. Re-isolating left traced artwork with no double-click way
      // out at all — a trace is a partition, so every point belongs to some
      // object, and every attempt to double-click clear of the current scope
      // dropped you straight into a new one with the rest of the art still
      // dimmed. Isolation looked impossible to leave.
      if (isolationScope?.objectId && (isolationScope.layerId !== layer.id || isolationScope.objectId !== object?.id)) {
        onIsolationScope(null);
        if (object) {
          history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
          onVectorObjectSelection({ layerId: layer.id, objectIds: [object.id], scope: 'object' });
        }
        return;
      }
      if (object && (isolationScope?.layerId !== layer.id || isolationScope.objectId !== object.id)) {
        history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
        onVectorObjectSelection({ layerId: layer.id, objectIds: [object.id], scope: 'object' });
        onIsolationScope({ layerId: layer.id, objectId: object.id });
        return;
      }
      if (object?.type === 'path') {
        onVectorObjectSelection({ layerId: layer.id, objectIds: [object.id], scope: 'node', nodeIds: [] });
        return;
      }
    }
    // Nothing was hit. While isolated that is the "double-click outside to get
    // out" gesture, and it must not fall through to creating a text layer:
    // double-clicking away from the isolated scope used to spawn an empty text
    // layer and leave the mode running, so trying to escape made new layers.
    if (isolationScope) {
      onIsolationScope(null);
      return;
    }
    void import('../vector/index').then((vector) => {
      if (!mounted.current) return;
      vector.activateVectorSubsystem();
      const layer = vector.createTextLayer();
      layer.text.value = '';
      layer.transform = [1, 0, 0, 1, point.x, point.y];
      history.execute(document, new AddLayerCommand(layer));
      beginTextEditing(layer);
    });
  };

  const displayRect = (rect: Rect) => ({
    x: view.panX + Math.min(rect.x, rect.x + rect.width) * view.zoom,
    y: view.panY + Math.min(rect.y, rect.y + rect.height) * view.zoom,
    width: Math.abs(rect.width * view.zoom),
    height: Math.abs(rect.height * view.zoom),
  });

  const effectiveTool = temporaryHand ? 'hand' : tool;
  const cloneSourceMarker = effectiveTool === 'clone-stamp' && cloneSource ? (() => {
    const transform = layerDocumentTransform(document.root, cloneSource.layerId);
    if (!transform) return null;
    const point = transformPoint(transform, cloneSource.point);
    return { x: view.panX + point.x * view.zoom, y: view.panY + point.y * view.zoom };
  })() : null;
  const positions = (limit: number) => {
    const result: number[] = [];
    for (let value = 0; value <= limit && result.length < 2_000; value += gridSpacing) result.push(value);
    return result;
  };
  const verticalGrid = positions(document.width);
  const horizontalGrid = positions(document.height);
  const frameTarget = vectorObjectSelection?.scope === 'node' ? null : selectedTransformTarget();
  const editingLayer = textEditor ? findLayer(document.root, textEditor.layerId) : undefined;
  const editingTransform = editingLayer?.kind === 'text' ? layerDocumentTransform(document.root, editingLayer.id) : null;
  const transformFrame = effectiveTool === 'move' && frameTarget && frameTarget.bounds.width > 0 && frameTarget.bounds.height > 0 ? (() => {
    const effectiveTransform = multiplyMatrix(frameTarget.parentTransform, frameTarget.transform);
    const toScreen = (point: Point): Point => ({ x: view.panX + point.x * view.zoom, y: view.panY + point.y * view.zoom });
    const screenHandle = (handle: ResizeHandle) => toScreen(transformPoint(effectiveTransform, handlePoint(frameTarget.bounds, handle)));
    const handles = resizeHandles.map((handle) => ({ handle, point: screenHandle(handle) }));
    const corners = (['nw', 'ne', 'se', 'sw'] as const).map(screenHandle);
    const center = toScreen(transformPoint(effectiveTransform, {
      x: frameTarget.bounds.x + frameTarget.bounds.width / 2,
      y: frameTarget.bounds.y + frameTarget.bounds.height / 2,
    }));
    const top = screenHandle('n');
    const outwardLength = Math.hypot(top.x - center.x, top.y - center.y) || 1;
    const rotate = {
      x: top.x + (top.x - center.x) / outwardLength * 28,
      y: top.y + (top.y - center.y) / outwardLength * 28,
    };
    return { handles, corners, top, rotate, locked: frameTarget.layer.locked || frameTarget.layer.positionLocked, object: frameTarget.object };
  })() : null;
  const pathNodes = (() => {
    if (vectorObjectSelection?.scope !== 'node') return [];
    const layer = findLayer(document.root, vectorObjectSelection.layerId);
    const object = layer?.kind === 'vector' ? layer.vector.objects.find((item) => item.id === vectorObjectSelection.objectIds[0]) : undefined;
    const layerTransform = layer?.kind === 'vector' ? layerDocumentTransform(document.root, layer.id) : null;
    if (object?.type !== 'path' || !layerTransform) return [];
    const effective = multiplyMatrix(layerTransform, object.transform);
    return object.geometry.nodes.map((node) => {
      const point = transformPoint(effective, node.anchor);
      return { id: node.id, x: view.panX + point.x * view.zoom, y: view.panY + point.y * view.zoom, selected: vectorObjectSelection.nodeIds?.includes(node.id) ?? false };
    });
  })();
  const penPreviewPoints = [...penDraft.map((node) => node.anchor), ...(penPointer ? [penPointer] : [])].map((point) => `${view.panX + point.x * view.zoom},${view.panY + point.y * view.zoom}`).join(' ');
  const shaperPreviewPoints = shaperPoints.map((point) => `${view.panX + point.x * view.zoom},${view.panY + point.y * view.zoom}`).join(' ');
  const isolationLayer = isolationScope ? findLayer(document.root, isolationScope.layerId) : undefined;
  const isolationObjectName = isolationScope?.objectId && isolationLayer?.kind === 'vector' ? isolationLayer.vector.objects.find((object) => object.id === isolationScope.objectId)?.name : undefined;
  return (
    <div
      ref={hostRef}
      className={`canvas-host cursor-${effectiveTool} ${maskEditingLayerId ? 'mask-editing' : ''} ${toolBlockedReason && effectiveTool === tool ? 'cursor-blocked' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => finishInteraction(event)}
      onPointerCancel={(event) => finishInteraction(event, true)}
      onPointerLeave={() => setCursor(null)}
      onDoubleClick={onDoubleClick}
      role="application"
      aria-label={`Canvas for ${document.name}`}
      aria-description={toolBlockedReason ?? undefined}
    >
      <canvas ref={canvasRef} className={`display-canvas ${isolationScope ? 'isolation-dimmed' : ''}`} />
      {isolatedView ? <canvas ref={isolationCanvasRef} className="display-canvas isolation-canvas" /> : null}
      <canvas ref={selectionCanvasRef} className="display-canvas object-selection-canvas" aria-hidden="true" />
      {isolationScope ? <div className="isolation-banner"><span>Isolation Mode › {isolationObjectName ?? isolationLayer?.name ?? 'Editing scope'}</span><button type="button" onClick={() => onIsolationScope(null)}>Exit</button></div> : null}
      {quickMaskActive && (document.selection.inverted || document.selection.regions.length > 0) ? <svg className="quick-mask-overlay" aria-hidden="true">
        <defs><mask id={`quick-mask-${document.id}`}><rect x={view.panX} y={view.panY} width={document.width * view.zoom} height={document.height * view.zoom} fill="white" />{document.selection.inverted ? null : document.selection.regions.map((rect, index) => <rect key={`${rect.x}:${rect.y}:${index}`} {...displayRect(rect)} fill="black" />)}</mask></defs>
        {document.selection.inverted
          ? document.selection.regions.map((rect, index) => <rect key={`${rect.x}:${rect.y}:${index}`} {...displayRect(rect)} className="quick-mask-fill" />)
          : <rect x={view.panX} y={view.panY} width={document.width * view.zoom} height={document.height * view.zoom} className="quick-mask-fill" mask={`url(#quick-mask-${document.id})`} />}
      </svg> : null}
      {textEditor && editingLayer?.kind === 'text' && editingTransform ? <textarea
        className="direct-text-editor"
        autoFocus
        aria-label={`Edit ${editingLayer.name}`}
        value={textEditor.value}
        style={{
          width: editingLayer.text.box.width,
          height: layerBounds(editingLayer)?.height ?? editingLayer.text.box.height,
          fontFamily: editingLayer.text.style.family,
          fontSize: editingLayer.text.style.size,
          fontWeight: editingLayer.text.style.weight,
          lineHeight: editingLayer.text.style.lineHeight,
          letterSpacing: editingLayer.text.style.tracking,
          textAlign: editingLayer.text.style.align,
          // The canvas repaints live on every keystroke and is the single
          // source of truth; the overlay renders no glyphs of its own (they
          // would double against the canvas), only the caret and selection.
          color: 'transparent',
          caretColor: editingLayer.text.style.color,
          transform: `matrix(${editingTransform[0] * view.zoom},${editingTransform[1] * view.zoom},${editingTransform[2] * view.zoom},${editingTransform[3] * view.zoom},${view.panX + editingTransform[4] * view.zoom},${view.panY + editingTransform[5] * view.zoom})`,
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          const value = event.target.value;
          setTextEditor((current) => current ? { ...current, value } : current);
          history.updateLive(document, (command) => { if (isTextEditCommand(command)) command.updateText(document, value); });
        }}
        onBlur={() => { history.commitLive(document); setTextEditor(null); }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.preventDefault(); history.cancelLive(document); setTextEditor(null); }
          else if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
        }}
      /> : null}
      {maskEditingLayerId ? <div className="mask-editing-banner">Editing layer mask · grayscale controls visibility</div> : null}
      <svg className="selection-overlay" aria-hidden="true">
        {showGrid ? <g className="canvas-grid">
          {verticalGrid.map((position) => <line key={`vx:${position}`} x1={view.panX + position * view.zoom} x2={view.panX + position * view.zoom} y1={view.panY} y2={view.panY + document.height * view.zoom} />)}
          {horizontalGrid.map((position) => <line key={`hy:${position}`} x1={view.panX} x2={view.panX + document.width * view.zoom} y1={view.panY + position * view.zoom} y2={view.panY + position * view.zoom} />)}
        </g> : null}
        {showGuides ? <g className="canvas-guides">
          {(document.guides ?? []).map((guide) => guide.axis === 'vertical'
            ? <line key={guide.id} x1={view.panX + guide.position * view.zoom} x2={view.panX + guide.position * view.zoom} y1={view.panY} y2={view.panY + document.height * view.zoom} />
            : <line key={guide.id} x1={view.panX} x2={view.panX + document.width * view.zoom} y1={view.panY + guide.position * view.zoom} y2={view.panY + guide.position * view.zoom} />)}
        </g> : null}
        {artboards.length ? <g className="artboard-overlays">{artboards.map((artboard) => {
          const shown = displayRect(artboard.rect);
          return <g key={artboard.id}><rect {...shown} /><text x={shown.x + 4} y={shown.y - 5}>{artboard.name}</text></g>;
        })}</g> : null}
        {notes.length ? <g className="note-overlays">{notes.map((note, index) => <g key={note.id} className={`${note.resolved ? 'resolved' : ''} note-${note.color}`} transform={`translate(${view.panX + note.x * view.zoom} ${view.panY + note.y * view.zoom})`}><circle r="9" /><text x="0" y="3">{index + 1}</text></g>)}</g> : null}
        {perspectiveGrid.length ? <g className="perspective-grid">{perspectiveGrid.map(({ id, line }) => <polyline key={id} points={line.map((point) => `${view.panX + point.x * view.zoom},${view.panY + point.y * view.zoom}`).join(' ')} />)}</g> : null}
        {perspectiveHandlePlane ? <g className={`perspective-handles ${validPerspectiveQuad(perspectiveHandlePlane.corners) ? '' : 'invalid'}`}>{perspectiveHandlePlane.corners.map((corner, index) => <circle
          key={index}
          cx={view.panX + corner.x * view.zoom}
          cy={view.panY + corner.y * view.zoom}
          r="6"
          role="button"
          aria-label={`Perspective corner ${index + 1}`}
          onPointerDown={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (event.button !== 0) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            planeDraftRef.current = perspectiveHandlePlane.corners;
            setPlaneDraft({ planeId: perspectiveHandlePlane.id, corners: perspectiveHandlePlane.corners });
            planeDragIndex.current = index;
          }}
          onPointerMove={(event) => {
            if (planeDragIndex.current !== index) return;
            const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
            if (!bounds) return;
            const next = {
              x: (event.clientX - bounds.left - view.panX) / view.zoom,
              y: (event.clientY - bounds.top - view.panY) / view.zoom,
            };
            const corners = (planeDraftRef.current ?? perspectiveHandlePlane.corners)
              .map((point, pointIndex) => (pointIndex === index ? next : point)) as unknown as Quad;
            planeDraftRef.current = corners;
            setPlaneDraft({ planeId: perspectiveHandlePlane.id, corners });
          }}
          onPointerUp={(event) => {
            if (planeDragIndex.current !== index) return;
            planeDragIndex.current = null;
            event.currentTarget.releasePointerCapture(event.pointerId);
            const corners = planeDraftRef.current;
            planeDraftRef.current = null;
            setPlaneDraft(null);
            // Committed outside the state updater: updaters must stay pure, and
            // StrictMode double-invokes them, which produced two history entries.
            if (corners && validPerspectiveQuad(corners)) {
              const all = documentPerspectivePlanes(document);
              const activeId = activePerspectivePlane(document)?.id;
              history.execute(document, new SetPerspectivePlanesCommand(
                all.map((plane) => (plane.id === perspectiveHandlePlane.id ? { ...plane, corners } : plane)),
                activeId,
              ));
            }
          }}
          onPointerCancel={() => {
            planeDragIndex.current = null;
            planeDraftRef.current = null;
            setPlaneDraft(null);
          }}
        />)}</g> : null}
        {smartGuides ? <g className="smart-guides">
          {smartGuides.vertical === undefined ? null : <line x1={view.panX + smartGuides.vertical * view.zoom} x2={view.panX + smartGuides.vertical * view.zoom} y1={view.panY} y2={view.panY + document.height * view.zoom} />}
          {smartGuides.horizontal === undefined ? null : <line x1={view.panX} x2={view.panX + document.width * view.zoom} y1={view.panY + smartGuides.horizontal * view.zoom} y2={view.panY + smartGuides.horizontal * view.zoom} />}
          {smartGuides.labels?.map((label, index) => <text key={`${label}:${index}`} x={Math.max(8, view.panX + 8)} y={Math.max(16, view.panY + 18 + index * 16)}>{label}</text>)}
        </g> : null}
        {!quickMaskActive && document.selection.regions.map((rect, index) => {
          const shown = displayRect(rect);
          return <rect key={`${rect.x}:${rect.y}:${index}`} className="selection-ants" {...shown} />;
        })}
        {marquee ? <rect className="selection-preview" {...displayRect(marquee)} /> : null}
        {shapePreview?.kind === 'rectangle' || shapePreview?.kind === 'rectangular-grid' ? <rect className="shape-draw-preview" {...displayRect(shapePreview.rect)} rx={shapePreview.kind === 'rectangle' ? Math.min(18, shapePreview.rect.width / 4, shapePreview.rect.height / 4) * view.zoom : 0} /> : null}
        {shapePreview?.kind === 'ellipse' || shapePreview?.kind === 'polar-grid' ? (() => {
          const shown = displayRect(shapePreview.rect);
          return <ellipse className="shape-draw-preview" cx={shown.x + shown.width / 2} cy={shown.y + shown.height / 2} rx={shown.width / 2} ry={shown.height / 2} />;
        })() : null}
        {gradient ? <g className="gradient-preview"><line x1={view.panX + gradient.start.x * view.zoom} y1={view.panY + gradient.start.y * view.zoom} x2={view.panX + gradient.end.x * view.zoom} y2={view.panY + gradient.end.y * view.zoom} /><circle cx={view.panX + gradient.start.x * view.zoom} cy={view.panY + gradient.start.y * view.zoom} r="3" /><circle cx={view.panX + gradient.end.x * view.zoom} cy={view.panY + gradient.end.y * view.zoom} r="3" /></g> : null}
        {knifePreview ? <line className="knife-preview" x1={view.panX + knifePreview.start.x * view.zoom} y1={view.panY + knifePreview.start.y * view.zoom} x2={view.panX + knifePreview.end.x * view.zoom} y2={view.panY + knifePreview.end.y * view.zoom} /> : null}
        {pathNodes.length ? <g className="path-nodes">{pathNodes.map((node) => <circle key={node.id} className={node.selected ? 'selected' : ''} cx={node.x} cy={node.y} r={node.selected ? 5 : 4} />)}</g> : null}
        {penPreviewPoints ? <g className="pen-preview"><polyline points={penPreviewPoints} />{penDraft.map((node, index) => <circle key={index} cx={view.panX + node.anchor.x * view.zoom} cy={view.panY + node.anchor.y * view.zoom} r="4" />)}</g> : null}
        {shaperPreviewPoints ? <polyline className="shaper-preview" points={shaperPreviewPoints} /> : null}
        {cloneSourceMarker ? <g className="clone-source-marker"><circle cx={cloneSourceMarker.x} cy={cloneSourceMarker.y} r="8" /><line x1={cloneSourceMarker.x - 11} y1={cloneSourceMarker.y} x2={cloneSourceMarker.x + 11} y2={cloneSourceMarker.y} /><line x1={cloneSourceMarker.x} y1={cloneSourceMarker.y - 11} x2={cloneSourceMarker.x} y2={cloneSourceMarker.y + 11} /></g> : null}
      </svg>
      {transformFrame ? <svg className={`transform-overlay ${transformFrame.locked ? 'locked' : ''}`} data-transform-scope={frameTarget?.collectiveObjectIds ? 'constellation' : transformFrame.object ? 'object' : 'layer'} aria-hidden="true">
        <polygon className="transform-frame" points={transformFrame.corners.map((point) => `${point.x},${point.y}`).join(' ')} />
        {!transformFrame.locked ? <>
          <line className="transform-rotate-stem" x1={transformFrame.top.x} y1={transformFrame.top.y} x2={transformFrame.rotate.x} y2={transformFrame.rotate.y} />
          <circle className="transform-rotate-handle" data-transform-handle="rotate" cx={transformFrame.rotate.x} cy={transformFrame.rotate.y} r="5" />
          {transformFrame.handles.map(({ handle, point }) => <rect
            key={handle}
            className={`transform-handle transform-handle-${handle}`}
            data-transform-handle={handle}
            x={point.x - 5}
            y={point.y - 5}
            width="10"
            height="10"
            rx="1"
          />)}
        </> : null}
      </svg> : null}
      {showRulers ? <>
        <div className="canvas-ruler ruler-horizontal" style={{ left: view.panX, top: Math.max(0, view.panY - 19), width: document.width * view.zoom }}>
          {verticalGrid.map((position) => <span key={position} style={{ left: position * view.zoom }}><i /><em>{position}</em></span>)}
        </div>
        <div className="canvas-ruler ruler-vertical" style={{ left: Math.max(0, view.panX - 19), top: view.panY, height: document.height * view.zoom }}>
          {horizontalGrid.map((position) => <span key={position} style={{ top: position * view.zoom }}><i /><em>{position}</em></span>)}
        </div>
      </> : null}
      {cursor && (effectiveTool === 'quick-selection' || effectiveTool === 'brush' || effectiveTool === 'history-brush' || effectiveTool === 'clone-stamp' || effectiveTool === 'healing-brush' || effectiveTool === 'dodge' || effectiveTool === 'burn' || effectiveTool === 'eraser') ? (
        <div
          className="brush-cursor"
          style={{ left: cursor.x, top: cursor.y, width: Math.max(3, brush.size * view.zoom), height: Math.max(3, brush.size * view.zoom) }}
        />
      ) : null}
      <div className="canvas-zoom-controls" aria-label="Canvas zoom">
        <button type="button" aria-label="Zoom out" onClick={() => zoomAt({ x: size.width / 2, y: size.height / 2 }, 0.8)}><Minus size={14} /></button>
        <button type="button" className="zoom-readout" onClick={fit} title="Fit canvas">{Math.round(view.zoom * 100)}%</button>
        <button type="button" aria-label="Zoom in" onClick={() => zoomAt({ x: size.width / 2, y: size.height / 2 }, 1.25)}><Plus size={14} /></button>
        <button type="button" aria-label="Fit canvas" onClick={fit}><Maximize2 size={14} /></button>
      </div>
    </div>
  );
}
